import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { toast } from 'sonner';
import { formatDate, getInitials, leadSourceColors, leadStatusColors } from '../lib/utils';
import {
  GripVertical,
  Phone,
  Mail,
  Calendar,
  MoreVertical,
  Eye,
  Edit,
  Trash2,
  UserPlus,
  PhoneCall,
  MessageCircle,
  Video,
  Users,
  AtSign,
  StickyNote,
  CalendarClock,
  Clock,
  User,
  Link2,
  CalendarPlus,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { useAuth } from '../contexts/AuthContext';
import { useBusinessLine, BUSINESS_LINES } from '../contexts/BusinessLineContext';
import { PolicyConversionModal } from '../components/PolicyConversionModal';
import { Calendar as CalendarPicker } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTIVE_STATUSES = ['nuevo', 'contactado', 'en_negociacion', 'pendiente_de_pago'];
const MONTH_LABELS = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

function getLeadMonthKey(lead) {
  const dateStr = lead.updated_at || lead.created_at;
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

const PIPELINE_STAGES = [
  { id: 'nuevo', label: 'Nuevo', color: 'bg-blue-500' },
  { id: 'contactado', label: 'Contactado', color: 'bg-yellow-500' },
  { id: 'en_negociacion', label: 'En Negociación', color: 'bg-purple-500' },
  { id: 'pendiente_de_pago', label: 'Pendiente de pago', color: 'bg-orange-500' },
  { id: 'ganado', label: 'Ganado', color: 'bg-green-500' },
  { id: 'perdido', label: 'Perdido', color: 'bg-red-500' },
];

const CONTACT_TYPES = [
  { value: 'llamada', label: 'Llamada', Icon: PhoneCall },
  { value: 'whatsapp', label: 'WhatsApp', Icon: MessageCircle },
  { value: 'videollamada', label: 'Videollamada', Icon: Video },
  { value: 'reunion_presencial', label: 'Reunión presencial', Icon: Users },
  { value: 'email', label: 'Email', Icon: AtSign },
];

const DAYS_BEFORE_OPTIONS = [
  { value: '30min', label: '30 minutos antes' },
  { value: '1h',    label: '1 hora antes' },
  { value: '1d',    label: '1 día antes' },
  { value: '3d',    label: '3 días antes' },
  { value: '7d',    label: '7 días antes' },
];
const DAYS_MAP = { '30min': 0, '1h': 0, '1d': 1, '3d': 3, '7d': 7 };
const toYMD = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// Cita can only be scheduled for active (non-closed) pipeline stages
const CITA_ALLOWED_STATUSES = ['nuevo', 'contactado', 'en_negociacion'];

const DURACION_OPTIONS = [
  { value: '15min',  label: '15 minutos', mins: 15  },
  { value: '30min',  label: '30 minutos', mins: 30  },
  { value: '1hr',    label: '1 hora',     mins: 60  },
  { value: '1.5hr',  label: '1.5 horas',  mins: 90  },
  { value: '2hr',    label: '2 horas',    mins: 120 },
  { value: '3hr',    label: '3 horas',    mins: 180 },
];

const RECURRENCE_OPTIONS = [
  { value: 'none',     label: 'Ninguna (solo esta vez)'     },
  { value: 'daily',    label: 'Diaria'                      },
  { value: 'weekly',   label: 'Semanal'                     },
  { value: 'monthly',  label: 'Mensual'                     },
  { value: 'weekdays', label: 'Días entre semana (lun-vie)' },
];

function computeEndDatetime(fechaStr, horaStr, duracionValue) {
  const start = new Date(`${fechaStr}T${horaStr}`);
  const mins = DURACION_OPTIONS.find(d => d.value === duracionValue)?.mins ?? 30;
  return new Date(start.getTime() + mins * 60000).toISOString();
}

const STATUS_LABELS = {
  nuevo: 'Nuevo',
  contactado: 'Contactado',
  en_negociacion: 'En Negociación',
  pendiente_de_pago: 'Pendiente de pago',
  ganado: 'Ganado',
  perdido: 'Perdido',
};

function getContactTypeIcon(tipo) {
  const found = CONTACT_TYPES.find(ct => ct.value === tipo);
  if (!found) return null;
  const { Icon } = found;
  return <Icon className="h-3 w-3" />;
}

function getContactTypeLabel(tipo) {
  return CONTACT_TYPES.find(ct => ct.value === tipo)?.label || tipo;
}

function truncate(str, max) {
  if (!str) return '';
  return str.length > max ? str.slice(0, max) + '…' : str;
}

const fmtShortDate = (dateStr) => {
  if (!dateStr) return '—';
  const hasTime = dateStr.includes('T') || dateStr.includes(' ');
  if (hasTime) {
    const d = new Date(
      dateStr.endsWith('Z') || dateStr.includes('+') ? dateStr : dateStr + 'Z'
    );
    return d.toLocaleDateString('es-MX', {
      day: '2-digit', month: 'short', year: 'numeric',
      timeZone: 'America/Mexico_City',
    });
  }
  const d = new Date(dateStr + 'T12:00:00Z');
  return d.toLocaleDateString('es-MX', {
    day: '2-digit', month: 'short', year: 'numeric',
    timeZone: 'America/Mexico_City',
  });
};

// ── Status Change Modal ──────────────────────────────────────────────────────
const StatusChangeModal = ({ open, onOpenChange, lead, newStatus, onConfirm }) => {
  const [tipoContacto, setTipoContacto] = useState('');
  const [nota, setNota] = useState('');
  const [proximaFecha, setProximaFecha] = useState('');
  const [diasAnticipacion, setDiasAnticipacion] = useState('1d');
  const [motivoRecordatorio, setMotivoRecordatorio] = useState('');
  // Cita fields
  const [agendarCita, setAgendarCita] = useState(false);
  const [citaFecha, setCitaFecha] = useState('');
  const [citaHora, setCitaHora] = useState('10:00');
  const [citaLink, setCitaLink] = useState('');
  const [citaDuracion, setCitaDuracion] = useState('30min');
  const [citaRecurrencia, setCitaRecurrencia] = useState('none');
  const [citaRepeatUntil, setCitaRepeatUntil] = useState('');
  const [fechaError, setFechaError] = useState('');
  const [proximaFechaOpen, setProximaFechaOpen] = useState(false);

  const canAgendarCita = CITA_ALLOWED_STATUSES.includes(newStatus);

  useEffect(() => {
    if (open) {
      setTipoContacto('');
      setNota('');
      setProximaFecha('');
      setDiasAnticipacion('1d');
      setMotivoRecordatorio('');
      setAgendarCita(false);
      setCitaFecha('');
      setCitaHora('10:00');
      setCitaLink('');
      setCitaDuracion('30min');
      setCitaRecurrencia('none');
      setCitaRepeatUntil('');
      setFechaError('');
      setProximaFechaOpen(false);
    }
  }, [open]);

  const handleConfirm = () => {
    if (!proximaFecha) {
      setFechaError('La próxima fecha de contacto es obligatoria');
      return;
    }
    setFechaError('');
    const recurrencia = citaRecurrencia !== 'none' ? citaRecurrencia : null;
    if (agendarCita && canAgendarCita && recurrencia && !citaRepeatUntil) {
      toast.error('Indica la fecha "Repetir hasta" para la recurrencia');
      return;
    }
    const citaData = (agendarCita && canAgendarCita && citaFecha)
      ? { fecha: citaFecha, hora: citaHora, link: citaLink, duracion: citaDuracion,
          recurrence: recurrencia,
          repeat_until: recurrencia ? citaRepeatUntil : undefined }
      : null;

    onConfirm({
      tipo_contacto: tipoContacto || null,
      nota_seguimiento: nota || null,
      next_follow_up_date: proximaFecha || null,
      follow_up_days_before: DAYS_MAP[diasAnticipacion] ?? 1,
      follow_up_reason: motivoRecordatorio || null,
      citaData,
    });
    onOpenChange(false);
  };

  const stageName = PIPELINE_STAGES.find(s => s.id === newStatus)?.label || newStatus;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Mover a "{stageName}"</DialogTitle>
          <DialogDescription>
            {lead?.name} — Registra el tipo de contacto y programa el siguiente seguimiento
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Tipo de contacto */}
          <div className="space-y-1.5">
            <Label>Tipo de contacto</Label>
            <Select value={tipoContacto} onValueChange={setTipoContacto}>
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar tipo de contacto..." />
              </SelectTrigger>
              <SelectContent>
                {CONTACT_TYPES.map(ct => {
                  const { Icon } = ct;
                  return (
                    <SelectItem key={ct.value} value={ct.value}>
                      <span className="flex items-center gap-2">
                        <Icon className="h-4 w-4" />
                        {ct.label}
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          {/* Nota */}
          <div className="space-y-1.5">
            <Label>Nota de seguimiento</Label>
            <Textarea
              value={nota}
              onChange={e => setNota(e.target.value)}
              placeholder="Describe el contacto o situación actual..."
              rows={3}
            />
          </div>

          {/* Próxima fecha de contacto */}
          <div className="space-y-1.5">
            <Label>Próxima fecha de contacto *</Label>
            <Popover open={proximaFechaOpen} onOpenChange={setProximaFechaOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start text-left font-normal">
                  <Calendar className="mr-2 h-4 w-4 text-muted-foreground" />
                  {proximaFecha || <span className="text-muted-foreground">Seleccionar fecha</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <CalendarPicker
                  mode="single"
                  selected={proximaFecha ? new Date(proximaFecha + 'T00:00:00') : undefined}
                  onSelect={(date) => {
                    if (date) {
                      setProximaFecha(toYMD(date));
                      if (fechaError) setFechaError('');
                    }
                    setProximaFechaOpen(false);
                  }}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            {fechaError && <p className="text-xs text-red-500">{fechaError}</p>}
          </div>

          {proximaFecha && (
            <div className="border rounded-lg p-3 space-y-3 bg-muted/30">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Recordatorio</p>
              <div className="space-y-1.5">
                <Label>Días de anticipación</Label>
                <Select value={diasAnticipacion} onValueChange={setDiasAnticipacion}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DAYS_BEFORE_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Motivo del recordatorio</Label>
                <Input
                  value={motivoRecordatorio}
                  onChange={e => setMotivoRecordatorio(e.target.value)}
                  placeholder='ej. "Auto pendiente de pago"'
                />
              </div>
            </div>
          )}

          {/* Agendar cita — solo para etapas activas */}
          {canAgendarCita && (
            <div className="border rounded-lg overflow-hidden">
              <div
                className="flex items-center gap-3 p-3 cursor-pointer hover:bg-muted/40 transition-colors"
                onClick={() => setAgendarCita(v => !v)}
              >
                <Checkbox
                  checked={agendarCita}
                  onCheckedChange={setAgendarCita}
                  onClick={e => e.stopPropagation()}
                  id="agendar-cita-check"
                />
                <label htmlFor="agendar-cita-check" className="flex items-center gap-2 cursor-pointer text-sm font-medium">
                  <CalendarPlus className="h-4 w-4 text-[#1a365d]" />
                  Agendar cita en el calendario
                </label>
              </div>

              {agendarCita && (
                <div className="px-3 pb-3 space-y-3 border-t bg-muted/20">
                  <div className="grid grid-cols-2 gap-3 pt-3">
                    <div className="space-y-1.5">
                      <Label>Fecha de la cita <span className="text-red-500">*</span></Label>
                      <Input
                        type="date"
                        value={citaFecha}
                        onChange={e => setCitaFecha(e.target.value)}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Hora <span className="text-red-500">*</span></Label>
                      <Input
                        type="time"
                        value={citaHora}
                        onChange={e => setCitaHora(e.target.value)}
                      />
                    </div>
                    <div className="col-span-2 space-y-1.5">
                      <Label>Duración estimada</Label>
                      <Select value={citaDuracion} onValueChange={setCitaDuracion}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {DURACION_OPTIONS.map(d => (
                            <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2 space-y-1.5">
                      <Label>Repetir cita</Label>
                      <Select value={citaRecurrencia} onValueChange={v => { setCitaRecurrencia(v); setCitaRepeatUntil(''); }}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {RECURRENCE_OPTIONS.map(r => (
                            <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {citaRecurrencia !== 'none' && (
                      <div className="col-span-2 space-y-1.5">
                        <Label>Repetir hasta <span className="text-red-500">*</span></Label>
                        <Input
                          type="date"
                          value={citaRepeatUntil}
                          onChange={e => setCitaRepeatUntil(e.target.value)}
                          min={citaFecha || undefined}
                        />
                      </div>
                    )}
                    <div className="col-span-2 space-y-1.5">
                      <Label className="flex items-center gap-1.5">
                        <Link2 className="h-3.5 w-3.5" />
                        Link de videollamada
                      </Label>
                      <Input
                        value={citaLink}
                        onChange={e => setCitaLink(e.target.value)}
                        placeholder="https://meet.google.com/..."
                      />
                    </div>
                  </div>
                  {agendarCita && !citaFecha && (
                    <p className="text-xs text-amber-600 flex items-center gap-1">
                      <span>⚠</span> Ingresa fecha y hora para agendar la cita.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={handleConfirm} className="bg-[#1a365d] hover:bg-[#1a365d]/90">
            {agendarCita && citaFecha ? 'Guardar, mover y agendar cita' : 'Guardar y mover'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ── Lead Detail Modal (read-only) ────────────────────────────────────────────
const LeadDetailModal = ({ open, onOpenChange, lead }) => {
  if (!lead) return null;
  const statusClass = leadStatusColors[lead.status] || 'bg-gray-100 text-gray-800';
  const history = lead.status_history || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Avatar className="h-8 w-8">
              <AvatarFallback className="bg-[#1a365d] text-white text-xs">
                {getInitials(lead.name)}
              </AvatarFallback>
            </Avatar>
            {lead.name}
          </DialogTitle>
          <DialogDescription>
            <Badge className={`${statusClass} border-0 mt-1`}>
              {STATUS_LABELS[lead.status] || lead.status}
            </Badge>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Contacto */}
          <div className="grid grid-cols-2 gap-3">
            {lead.email && (
              <div className="flex items-center gap-2 text-sm">
                <Mail className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <span className="truncate">{lead.email}</span>
              </div>
            )}
            {lead.phone && (
              <div className="flex items-center gap-2 text-sm">
                <Phone className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <span>{lead.phone}</span>
              </div>
            )}
          </div>

          {/* Fechas */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-xs text-muted-foreground mb-0.5">Fecha de llegada</p>
              <p>{fmtShortDate(lead.created_at) || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-0.5">Fecha nacimiento</p>
              <p>{lead.birth_date || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-0.5">Próxima fecha contacto</p>
              <p>{lead.next_follow_up_date || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-0.5">Fecha contacto inicial</p>
              <p>{lead.contact_date || '—'}</p>
            </div>
          </div>

          {/* Fuente */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-xs text-muted-foreground mb-0.5">Fuente</p>
              <p>{lead.source || '—'}</p>
            </div>
            {lead.source_campaign && (
              <div>
                <p className="text-xs text-muted-foreground mb-0.5">Campaña</p>
                <p>{lead.source_campaign}</p>
              </div>
            )}
          </div>

          {/* Productos/Aseguradoras */}
          {(lead.business_types?.length > 0 || lead.business_lines?.length > 0) && (
            <div className="text-sm">
              {lead.business_lines?.length > 0 && (
                <div className="mb-2">
                  <p className="text-xs text-muted-foreground mb-1">Aseguradora(s)</p>
                  <div className="flex flex-wrap gap-1">
                    {lead.business_lines.map(bl => (
                      <Badge key={bl} variant="outline" className="text-xs">{bl}</Badge>
                    ))}
                  </div>
                </div>
              )}
              {lead.business_types?.length > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Tipo(s) de negocio</p>
                  <div className="flex flex-wrap gap-1">
                    {lead.business_types.map(bt => (
                      <Badge key={bt} variant="secondary" className="text-xs">{bt}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Financiero */}
          {(lead.contributions > 0 || lead.periodicidad || lead.plazo > 0) && (
            <div className="grid grid-cols-3 gap-3 text-sm bg-muted/30 rounded-lg p-3">
              <div>
                <p className="text-xs text-muted-foreground mb-0.5">Aportación</p>
                <p className="font-medium">{lead.contributions ? `$${lead.contributions.toLocaleString()}` : '—'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-0.5">Periodicidad</p>
                <p className="capitalize">{lead.periodicidad || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-0.5">Plazo</p>
                <p>{lead.plazo ? `${lead.plazo} años` : '—'}</p>
              </div>
            </div>
          )}

          {/* Notas */}
          {lead.notes && (
            <div className="text-sm">
              <p className="text-xs text-muted-foreground mb-0.5">Notas</p>
              <p className="whitespace-pre-wrap text-sm">{lead.notes}</p>
            </div>
          )}

          {/* Historial de estados */}
          {history.length > 0 && (
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                Historial de seguimiento
              </p>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {[...history].reverse().map((entry, i) => (
                  <div key={i} className="border rounded-lg p-2 text-sm bg-muted/20">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-1.5">
                        {entry.tipo_contacto && (
                          <span className="text-[#1a365d]">
                            {getContactTypeIcon(entry.tipo_contacto)}
                          </span>
                        )}
                        <Badge className={`${leadStatusColors[entry.estado] || 'bg-gray-100 text-gray-800'} border-0 text-[10px] px-1.5 py-0`}>
                          {STATUS_LABELS[entry.estado] || entry.estado}
                        </Badge>
                        {entry.tipo_contacto && (
                          <span className="text-xs text-muted-foreground">{getContactTypeLabel(entry.tipo_contacto)}</span>
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground">{entry.fecha}</span>
                    </div>
                    {entry.nota && (
                      <p className="text-xs text-muted-foreground italic">{entry.nota}</p>
                    )}
                    {entry.proxima_fecha && (
                      <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                        <CalendarClock className="h-3 w-3" />
                        Próximo contacto: {entry.proxima_fecha}
                        {entry.motivo && ` — ${entry.motivo}`}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ── Lead Edit Modal ──────────────────────────────────────────────────────────
const LeadEditModal = ({ open, onOpenChange, lead, users, availableLines, isAdmin, onSaved }) => {
  const [formData, setFormData] = useState({});
  const [newBusinessType, setNewBusinessType] = useState('');
  const [saving, setSaving] = useState(false);

  // Populate form when lead changes
  useEffect(() => {
    if (lead) {
      setFormData({
        name: lead.name || '',
        email: lead.email || '',
        phone: lead.phone || '',
        birth_date: lead.birth_date || '',
        contact_date: lead.contact_date || '',
        next_follow_up_date: lead.next_follow_up_date || '',
        source: lead.source || 'manual',
        source_campaign: lead.source_campaign || '',
        notes: lead.notes || '',
        business_lines: lead.business_lines || [],
        business_types: lead.business_types || [],
        aportaciones: (lead.contributions ?? '').toString(),
        periodicidad: lead.periodicidad || 'mensual',
        plazo: (lead.plazo && lead.plazo !== 0) ? lead.plazo.toString() : '',
        assigned_agent_id: lead.assigned_agent_id || '',
      });
      setNewBusinessType('');
    }
  }, [lead]);

  const toggleBusinessLine = (lineId) => {
    setFormData(prev => ({
      ...prev,
      business_lines: prev.business_lines?.includes(lineId)
        ? prev.business_lines.filter(id => id !== lineId)
        : [...(prev.business_lines || []), lineId],
    }));
  };

  const addBusinessType = () => {
    const trimmed = newBusinessType.trim();
    if (!trimmed) return;
    setFormData(prev => {
      const types = prev.business_types || [];
      if (types.includes(trimmed)) return prev;
      return { ...prev, business_types: [...types, trimmed] };
    });
    setNewBusinessType('');
  };

  const removeBusinessType = (type) => {
    setFormData(prev => ({
      ...prev,
      business_types: (prev.business_types || []).filter(t => t !== type),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...formData,
        contributions: parseFloat(formData.aportaciones) || 0,
        plazo: formData.plazo !== '' ? parseInt(formData.plazo) : null,
      };
      await axios.put(`${API}/leads/${lead.id}`, payload);
      toast.success('Lead actualizado');
      onOpenChange(false);
      onSaved();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar lead');
    } finally {
      setSaving(false);
    }
  };

  if (!lead) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar Lead</DialogTitle>
          <DialogDescription>Modifica la información de {lead.name}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-2">
              <Label htmlFor="edit-name">Nombre *</Label>
              <Input
                id="edit-name"
                value={formData.name || ''}
                onChange={e => setFormData({ ...formData, name: e.target.value })}
                placeholder="Nombre completo"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={formData.email || ''}
                onChange={e => setFormData({ ...formData, email: e.target.value })}
                placeholder="correo@ejemplo.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-phone">Teléfono</Label>
              <Input
                id="edit-phone"
                value={formData.phone || ''}
                onChange={e => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+52 55 1234 5678"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-contact-date">Fecha de Contacto *</Label>
              <Input
                id="edit-contact-date"
                type="date"
                value={formData.contact_date || ''}
                onChange={e => setFormData({ ...formData, contact_date: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-next-follow-up">Fecha Próximo Contacto *</Label>
              <Input
                id="edit-next-follow-up"
                type="date"
                value={formData.next_follow_up_date || ''}
                onChange={e => setFormData({ ...formData, next_follow_up_date: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-birth-date">Fecha Nacimiento</Label>
              <Input
                id="edit-birth-date"
                type="date"
                value={formData.birth_date || ''}
                onChange={e => setFormData({ ...formData, birth_date: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Fuente</Label>
              <Select value={formData.source || 'manual'} onValueChange={v => setFormData({ ...formData, source: v })}>
                <SelectTrigger><SelectValue placeholder="Fuente" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Llamada en frío">Llamada en frío</SelectItem>
                  <SelectItem value="facebook">Facebook</SelectItem>
                  <SelectItem value="instagram">Instagram</SelectItem>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  <SelectItem value="linkedin">LinkedIn</SelectItem>
                  <SelectItem value="youtube">YouTube</SelectItem>
                  <SelectItem value="tiktok">TikTok</SelectItem>
                  <SelectItem value="website">Sitio Web</SelectItem>
                  <SelectItem value="referido">Referido</SelectItem>
                  <SelectItem value="manual">Manual</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-campaign">Campaña (opcional)</Label>
              <Input
                id="edit-campaign"
                value={formData.source_campaign || ''}
                onChange={e => setFormData({ ...formData, source_campaign: e.target.value })}
                placeholder="Nombre de campaña"
              />
            </div>
            {isAdmin && (
              <div className="col-span-2 space-y-2">
                <Label>Agente</Label>
                <select
                  value={formData.assigned_agent_id || ''}
                  onChange={e => setFormData({ ...formData, assigned_agent_id: e.target.value })}
                  className="w-full border rounded px-3 py-2 text-sm bg-background"
                >
                  <option value="">— Sin asignar —</option>
                  {[...users].sort((a, b) => (a.full_name || '').localeCompare(b.full_name || '', 'es')).map(u => (
                    <option key={u.id} value={u.id}>{u.full_name} ({u.role})</option>
                  ))}
                </select>
              </div>
            )}
            <div className="col-span-2 space-y-2">
              <Label>Notas</Label>
              <Textarea
                value={formData.notes || ''}
                onChange={e => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Notas adicionales..."
                rows={3}
              />
            </div>
            <div className="col-span-2 space-y-2">
              <Label>Negocio/Tipo de Plan</Label>
              <div className="flex gap-2">
                <Input
                  value={newBusinessType}
                  onChange={e => setNewBusinessType(e.target.value)}
                  placeholder="Ej: OptiMaxx Plus, Vida, Auto..."
                  onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addBusinessType())}
                />
                <Button type="button" variant="outline" onClick={addBusinessType}>Agregar</Button>
              </div>
              {(formData.business_types || []).length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.business_types.map(type => (
                    <Badge
                      key={type}
                      variant="secondary"
                      className="cursor-pointer hover:bg-red-100"
                      onClick={() => removeBusinessType(type)}
                    >
                      {type} ×
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-aportaciones">Aportaciones (MXN)</Label>
              <Input
                id="edit-aportaciones"
                type="number"
                value={formData.aportaciones || ''}
                onChange={e => setFormData({ ...formData, aportaciones: e.target.value })}
                placeholder="5000"
                min="0"
              />
            </div>
            <div className="space-y-2">
              <Label>Periodicidad</Label>
              <Select value={formData.periodicidad || 'mensual'} onValueChange={v => setFormData({ ...formData, periodicidad: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="mensual">Mensual</SelectItem>
                  <SelectItem value="trimestral">Trimestral</SelectItem>
                  <SelectItem value="semestral">Semestral</SelectItem>
                  <SelectItem value="anual">Anual</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 space-y-2">
              <Label>Plazo (años: 5–25)</Label>
              <Input
                type="number"
                value={formData.plazo || ''}
                onChange={e => setFormData({ ...formData, plazo: e.target.value })}
                placeholder="15"
                min="5"
                max="25"
              />
            </div>
            <div className="col-span-2 space-y-2">
              <Label>Aseguradora *</Label>
              <div className="flex flex-wrap gap-3 p-3 border rounded-lg">
                {availableLines.filter(l => l.id !== 'all').map(line => (
                  <div key={line.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`edit-line-${line.id}`}
                      checked={(formData.business_lines || []).includes(line.id)}
                      onCheckedChange={() => toggleBusinessLine(line.id)}
                    />
                    <label
                      htmlFor={`edit-line-${line.id}`}
                      className="text-sm font-medium leading-none flex items-center gap-2 cursor-pointer"
                    >
                      <div className={`w-2 h-2 rounded-full ${line.color}`} />
                      {line.name}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button
              type="submit"
              className="bg-[#1a365d] hover:bg-[#1a365d]/90"
              disabled={saving || (formData.business_lines || []).length === 0}
            >
              {saving ? 'Guardando...' : 'Actualizar Lead'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ── Lead Card ────────────────────────────────────────────────────────────────
const LeadCard = ({ lead, onDragStart, onView, onEdit, onDelete, onConvert, canEdit, canCreate, canDelete, isAdmin, showAgentLabel }) => {
  const lastHistory = (lead.status_history || []).slice(-1)[0];
  const lastNota = lastHistory?.nota || '';
  const lastTipo = lastHistory?.tipo_contacto || '';
  const lastFechaHistorial = lastHistory?.fecha || '';
  const proximaFecha = lead.next_follow_up_date;

  // "Actualizado" only when there is at least one history entry
  const hasHistory = (lead.status_history || []).length > 0;

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, lead)}
      className="bg-card border rounded-lg p-3 mb-2 cursor-grab active:cursor-grabbing hover:shadow-md transition-all group"
      data-testid={`pipeline-lead-${lead.id}`}
    >
      {/* Header row */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <GripVertical className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
          <Avatar className="h-8 w-8">
            <AvatarFallback className="bg-[#1a365d] text-white text-xs">
              {getInitials(lead.name)}
            </AvatarFallback>
          </Avatar>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity">
              <MoreVertical className="h-3 w-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onView(lead)}>
              <Eye className="h-4 w-4 mr-2" />
              Ver detalles
            </DropdownMenuItem>
            {canCreate && lead.status !== 'ganado' && (
              <DropdownMenuItem onClick={() => onConvert(lead)}>
                <UserPlus className="h-4 w-4 mr-2" />
                Convertir a cliente
              </DropdownMenuItem>
            )}
            {canEdit && (
              <DropdownMenuItem onClick={() => onEdit(lead)}>
                <Edit className="h-4 w-4 mr-2" />
                Editar
              </DropdownMenuItem>
            )}
            {(isAdmin || canDelete) && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => onDelete(lead.id)} className="text-red-600">
                  <Trash2 className="h-4 w-4 mr-2" />
                  Eliminar
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Name */}
      <div className="flex items-center gap-1.5 flex-wrap mb-1">
        <h4 className="font-medium text-sm line-clamp-1">{lead.name}</h4>
        {lead.lead_type === 'agente' && (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300">
            Agente
          </span>
        )}
      </div>

      {lead.email && (
        <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1">
          <Mail className="h-3 w-3" />
          <span className="line-clamp-1">{lead.email}</span>
        </div>
      )}

      {lead.phone && (
        <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1">
          <Phone className="h-3 w-3" />
          <span>{lead.phone}</span>
        </div>
      )}

      {/* Última nota con tipo de contacto */}
      {lastNota && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-start gap-1 text-xs text-muted-foreground mt-1 cursor-default">
                {lastTipo ? (
                  <span className="text-[#1a365d] flex-shrink-0 mt-0.5">{getContactTypeIcon(lastTipo)}</span>
                ) : (
                  <StickyNote className="h-3 w-3 flex-shrink-0 mt-0.5" />
                )}
                <span className="italic line-clamp-1">{truncate(lastNota, 60)}</span>
              </div>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-xs">
              <p className="text-xs">{lastNota}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      {/* Próxima fecha de contacto */}
      {proximaFecha && (
        <div className="flex items-center gap-1 text-xs text-amber-600 mt-1">
          <CalendarClock className="h-3 w-3 flex-shrink-0" />
          <span>Próximo: {proximaFecha}</span>
        </div>
      )}

      {/* Business Types */}
      {lead.business_types && lead.business_types.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {lead.business_types.map(bt => {
            const color =
              lead.status === 'ganado'  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
              lead.status === 'perdido' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                                          'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
            return (
              <Badge key={bt} className={`${color} text-[10px] px-1.5 py-0 font-normal border-0`}>{bt}</Badge>
            );
          })}
        </div>
      )}

      {lead.estimated_value > 0 && (
        <div className="mt-1.5 text-xs font-medium text-green-600">
          ${lead.estimated_value.toLocaleString()}
        </div>
      )}

      {/* Fechas footer */}
      <div className="mt-2 pt-2 border-t space-y-0.5">
        <div className="flex items-center justify-between">
          <div className={`w-2 h-2 rounded-full ${leadSourceColors[lead.source] || 'bg-gray-400'}`} title={lead.source} />
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Calendar className="h-3 w-3" />
            <span title="Fecha de llegada">Llegó: {fmtShortDate(lead.created_at) || '—'}</span>
          </div>
        </div>
        {hasHistory && lastFechaHistorial && (
          <div className="flex justify-end">
            <div className="flex items-center gap-1 text-xs text-muted-foreground/70">
              <Clock className="h-3 w-3" />
              <span>Act: {fmtShortDate(lastFechaHistorial)}</span>
            </div>
          </div>
        )}
        {showAgentLabel && lead.assigned_agent_name && (
          <div className="flex justify-end">
            <span className="text-[10px] text-muted-foreground/60">● {lead.assigned_agent_name}</span>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Pipeline Column ──────────────────────────────────────────────────────────
const PipelineColumn = ({ stage, leads, onDrop, onDragOver, onDragLeave, isDragOver, onView, onEdit, onDelete, onConvert, canEdit, canDelete, canCreate, onDragStart, isAdmin, showAgentLabel }) => {
  const stageLeads = leads.filter(lead => lead.status === stage.id);
  const totalValue = stageLeads.reduce((sum, l) => sum + (l.estimated_value || 0), 0);

  return (
    <div
      className={`flex-shrink-0 w-72 flex flex-col h-full transition-all ${
        isDragOver ? 'ring-2 ring-[#2d8659] ring-offset-2' : ''
      }`}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={(e) => onDrop(e, stage.id)}
      data-testid={`pipeline-column-${stage.id}`}
    >
      <div className={`${stage.color} rounded-t-lg px-3 py-2`}>
        <div className="flex items-center justify-between text-white">
          <span className="font-medium text-sm">{stage.label}</span>
          <Badge variant="secondary" className="bg-white/20 text-white text-xs">{stageLeads.length}</Badge>
        </div>
        {totalValue > 0 && (
          <div className="text-white/80 text-xs mt-1">${totalValue.toLocaleString()}</div>
        )}
      </div>

      <ScrollArea className="flex-1 bg-muted/30 rounded-b-lg p-2 min-h-[400px]">
        {stageLeads.length === 0 ? (
          <div className="flex items-center justify-center h-20 text-muted-foreground text-sm border-2 border-dashed rounded-lg">
            Arrastra leads aquí
          </div>
        ) : (
          stageLeads.map(lead => (
            <LeadCard
              key={lead.id}
              lead={lead}
              onDragStart={onDragStart}
              onView={onView}
              onEdit={onEdit}
              onDelete={onDelete}
              onConvert={onConvert}
              canEdit={canEdit}
              canDelete={canDelete}
              canCreate={canCreate}
              isAdmin={isAdmin}
              showAgentLabel={showAgentLabel}
            />
          ))
        )}
      </ScrollArea>
    </div>
  );
};

// ── Pipeline Page ────────────────────────────────────────────────────────────
export const PipelinePage = () => {
  const { user, canCreate, canEdit, canDelete, isAdmin, isMaster, isAsistente } = useAuth();
  const showAgentLabel = isAdmin || user?.role === 'developer';
  const { selectedLine, filterByBusinessLine, availableLines } = useBusinessLine();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [dragOverStage, setDragOverStage] = useState(null);
  const [draggedLead, setDraggedLead] = useState(null);
  const draggedLeadRef = useRef(null);
  const [pipelineMode, setPipelineMode] = useState('active');
  const [historicMonth, setHistoricMonth] = useState('');
  const [agents, setAgents] = useState([]);
  const [managedUsers, setManagedUsers] = useState([]);
  const [agentFilter, setAgentFilter] = useState(user?.id || '');

  // Modals
  const [policyModalOpen, setPolicyModalOpen] = useState(false);
  const [leadToConvert, setLeadToConvert] = useState(null);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewLead, setViewLead] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingLead, setEditingLead] = useState(null);

  useEffect(() => {
    axios.get(`${API}/users`).then(r => {
      setUsers(r.data);
      if (isAsistente) {
        const managed = user?.managed_agents || [];
        if (managed.length > 0) {
          setManagedUsers(r.data.filter(u => managed.includes(u.id)));
        }
      }
    }).catch(() => {});
    if (isAdmin || isMaster) {
      axios.get(`${API}/agents`).then(r => setAgents(r.data)).catch(() => {});
    }
  }, [isAdmin, isMaster, isAsistente]);

  useEffect(() => {
    fetchLeads();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentFilter]);

  const fetchLeads = async () => {
    try {
      const params = new URLSearchParams({ include_converted: 'true' });
      if ((isAdmin || isMaster || isAsistente) && agentFilter && agentFilter !== 'all') {
        params.append('agent_id', agentFilter);
      }
      const response = await axios.get(`${API}/leads?${params.toString()}`);
      setLeads(response.data);
    } catch (error) {
      toast.error('Error al cargar leads');
    } finally {
      setLoading(false);
    }
  };

  const availableMonths = useMemo(() => {
    const months = new Set();
    leads.forEach(lead => {
      const key = getLeadMonthKey(lead);
      if (key) months.add(key);
    });
    return [...months].sort().reverse();
  }, [leads]);

  useEffect(() => {
    if (pipelineMode === 'historic' && !historicMonth && availableMonths.length > 0) {
      setHistoricMonth(availableMonths[0]);
    }
  }, [pipelineMode, availableMonths, historicMonth]);

  const handleDragStart = (e, lead) => {
    setDraggedLead(lead);
    draggedLeadRef.current = lead;
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, stageId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverStage(stageId);
  };

  const handleDragLeave = () => setDragOverStage(null);

  const handleDrop = async (e, newStatus) => {
    e.preventDefault();
    setDragOverStage(null);

    const currentLead = draggedLeadRef.current || draggedLead;
    draggedLeadRef.current = null;
    setDraggedLead(null);

    if (!currentLead || currentLead.status === newStatus) return;

    if (newStatus === 'ganado') {
      setLeadToConvert(currentLead);
      setPolicyModalOpen(true);
      return;
    }

    setPendingStatusChange({ lead: currentLead, newStatus });
    setStatusModalOpen(true);
  };

  const confirmStatusChange = async (followUpData) => {
    if (!pendingStatusChange) return;
    const { lead: currentLead, newStatus } = pendingStatusChange;
    const oldStatus = currentLead.status;

    // Separate cita data from lead follow-up data
    const { citaData, ...leadFollowUpData } = followUpData;

    setLeads(prev => prev.map(l => l.id === currentLead.id ? { ...l, status: newStatus } : l));

    try {
      await axios.put(`${API}/leads/${currentLead.id}`, { status: newStatus, ...leadFollowUpData });
      const stageName = PIPELINE_STAGES.find(s => s.id === newStatus)?.label || newStatus;
      toast.success(`Movido a "${stageName}"`);
      fetchLeads();
    } catch (error) {
      setLeads(prev => prev.map(l => l.id === currentLead.id ? { ...l, status: oldStatus } : l));
      toast.error('Error al actualizar estado');
      setPendingStatusChange(null);
      return;
    }

    // Create calendar event if cita was scheduled
    if (citaData?.fecha) {
      try {
        const startIso = new Date(`${citaData.fecha}T${citaData.hora}`).toISOString();
        const endIso   = computeEndDatetime(citaData.fecha, citaData.hora, citaData.duracion);
        const descParts = [];
        if (leadFollowUpData.nota_seguimiento) descParts.push(leadFollowUpData.nota_seguimiento);
        if (citaData.link) descParts.push(`Videollamada: ${citaData.link}`);

        await axios.post(`${API}/calendar/events`, {
          title:          `Cita con ${currentLead.name}`,
          start_datetime: startIso,
          end_datetime:   endIso,
          type:           'lead',
          description:    descParts.join('\n') || null,
          videoLink:      citaData.link || null,
          guests:         [],
          recurrence:     citaData.recurrence || null,
          repeat_until:   citaData.repeat_until || undefined,
        });
        toast.success('Cita agendada en el calendario');
      } catch {
        toast.error('Lead actualizado, pero no se pudo crear la cita en el calendario');
      }
    }

    setPendingStatusChange(null);
  };

  const handleView = (lead) => {
    setViewLead(lead);
    setViewModalOpen(true);
  };

  const handleEdit = (lead) => {
    setEditingLead(lead);
    setEditModalOpen(true);
  };

  const handleDelete = async (leadId) => {
    if (!window.confirm('¿Estás seguro de eliminar este lead?')) return;
    try {
      await axios.delete(`${API}/leads/${leadId}`);
      toast.success('Lead eliminado');
      fetchLeads();
    } catch (error) {
      toast.error('Error al eliminar lead');
    }
  };

  let filteredLeads = filterByBusinessLine(leads, 'business_lines');

  if (pipelineMode === 'active') {
    filteredLeads = filteredLeads.filter(lead => ACTIVE_STATUSES.includes(lead.status));
  } else if (pipelineMode === 'historic' && historicMonth) {
    filteredLeads = filteredLeads.filter(lead => getLeadMonthKey(lead) === historicMonth);
  }

  const totalLeads = filteredLeads.length;
  const wonLeads = filteredLeads.filter(l => l.status === 'ganado').length;
  const totalValue = filteredLeads.reduce((sum, l) => sum + (l.estimated_value || 0), 0);
  const wonValue = filteredLeads.filter(l => l.status === 'ganado').reduce((sum, l) => sum + (l.estimated_value || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a365d]"></div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col" data-testid="pipeline-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Pipeline de Ventas</h1>
          <p className="text-muted-foreground">Arrastra y suelta para cambiar el estado de los leads</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {(isAdmin || isMaster || isAsistente) && (
            <Select value={agentFilter} onValueChange={setAgentFilter}>
              <SelectTrigger className="w-[200px] text-sm">
                <SelectValue placeholder="Agente" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={user?.id || ''}>Mis Leads</SelectItem>
                {!isAsistente && <SelectItem value="all">Todos</SelectItem>}
                {[...(isAsistente ? managedUsers : agents)]
                  .filter(a => {
                    const uid = isAsistente ? a.id : a.user_id;
                    return uid !== user?.id;
                  })
                  .sort((a, b) => {
                    const na = isAsistente ? (a.full_name || '') : (a.user?.full_name || '');
                    const nb = isAsistente ? (b.full_name || '') : (b.user?.full_name || '');
                    return na.localeCompare(nb, 'es');
                  })
                  .map(a => {
                    const uid = isAsistente ? a.id : a.user_id;
                    const name = isAsistente ? a.full_name : (a.user?.full_name || a.user_id);
                    return (
                      <SelectItem key={uid} value={uid}>
                        {name}
                      </SelectItem>
                    );
                  })
                }
              </SelectContent>
            </Select>
          )}
          <Badge variant="outline" className="text-sm">{totalLeads} leads</Badge>
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-sm">
            {wonLeads} ganados
          </Badge>
        </div>
      </div>

      {/* Mode toggle */}
      <div className="flex items-center gap-3 mb-4">
        <div className="flex items-center border rounded-lg p-1 bg-muted/30">
          <button
            onClick={() => setPipelineMode('active')}
            className={`px-4 py-1.5 rounded text-sm font-medium transition-colors ${
              pipelineMode === 'active'
                ? 'bg-white dark:bg-gray-800 shadow-sm text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Activo
          </button>
          <button
            onClick={() => setPipelineMode('historic')}
            className={`px-4 py-1.5 rounded text-sm font-medium transition-colors ${
              pipelineMode === 'historic'
                ? 'bg-white dark:bg-gray-800 shadow-sm text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Histórico
          </button>
        </div>
        {pipelineMode === 'active' && (
          <span className="text-sm text-muted-foreground">
            Todos los leads sin cerrar — Nuevo, Contactado, En Negociación, Pendiente de pago
          </span>
        )}
        {pipelineMode === 'historic' && (
          <select
            value={historicMonth}
            onChange={e => setHistoricMonth(e.target.value)}
            className="border rounded px-2 py-1.5 text-sm bg-background"
          >
            {availableMonths.length === 0 && <option value="">Sin datos</option>}
            {availableMonths.map(m => {
              const [yr, mo] = m.split('-');
              return (
                <option key={m} value={m}>{MONTH_LABELS[parseInt(mo) - 1]} {yr}</option>
              );
            })}
          </select>
        )}
      </div>

      {/* Stats */}
      <Card className="mb-4">
        <CardContent className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Total Leads</p>
              <p className="text-2xl font-bold">{totalLeads}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Valor Total</p>
              <p className="text-2xl font-bold">${totalValue.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Ganados</p>
              <p className="text-2xl font-bold text-green-600">{wonLeads}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Valor Ganado</p>
              <p className="text-2xl font-bold text-green-600">${wonValue.toLocaleString()}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pipeline Board */}
      <div className="flex-1 overflow-x-auto pb-4">
        <div className="flex gap-4 h-full min-w-max">
          {PIPELINE_STAGES.map(stage => (
            <PipelineColumn
              key={stage.id}
              stage={stage}
              leads={filteredLeads}
              onDrop={handleDrop}
              onDragOver={(e) => handleDragOver(e, stage.id)}
              onDragLeave={handleDragLeave}
              isDragOver={dragOverStage === stage.id}
              onView={handleView}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onConvert={(lead) => { setLeadToConvert(lead); setPolicyModalOpen(true); }}
              canEdit={canEdit}
              canDelete={canDelete || isAsistente}
              canCreate={canCreate}
              onDragStart={handleDragStart}
              isAdmin={isAdmin}
              showAgentLabel={showAgentLabel}
            />
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 pt-4 border-t text-xs text-muted-foreground">
        <span className="font-medium">Fuentes:</span>
        {Object.entries(leadSourceColors).filter(([source]) => !['tiktok', 'linkedin', 'youtube'].includes(source)).slice(0, 6).map(([source, color]) => (
          <div key={source} className="flex items-center gap-1">
            <div className={`w-2 h-2 rounded-full ${color}`} />
            <span className="capitalize">{source}</span>
          </div>
        ))}
      </div>

      {/* Modals */}
      <PolicyConversionModal
        open={policyModalOpen}
        onOpenChange={(v) => { setPolicyModalOpen(v); if (!v) setLeadToConvert(null); }}
        lead={leadToConvert}
        apiUrl={API}
        onSuccess={() => fetchLeads()}
        isAdmin={isAdmin}
      />

      <StatusChangeModal
        open={statusModalOpen}
        onOpenChange={(v) => { setStatusModalOpen(v); if (!v) setPendingStatusChange(null); }}
        lead={pendingStatusChange?.lead}
        newStatus={pendingStatusChange?.newStatus}
        onConfirm={confirmStatusChange}
      />

      <LeadDetailModal
        open={viewModalOpen}
        onOpenChange={(v) => { setViewModalOpen(v); if (!v) setViewLead(null); }}
        lead={viewLead}
      />

      <LeadEditModal
        open={editModalOpen}
        onOpenChange={(v) => { setEditModalOpen(v); if (!v) setEditingLead(null); }}
        lead={editingLead}
        users={users}
        availableLines={availableLines || []}
        isAdmin={isAdmin}
        onSaved={fetchLeads}
      />
    </div>
  );
};
